import { ColumnState } from "ag-grid-community";
import { create } from "zustand";
import {
	defaultEQM2080_R01HeaderDto,
	EQM2080_R01DetailPivotDto,
	EQM2080_R01HeaderDto,
} from "./Dto_EQM2080_R01";
import { formatDate, formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";

export type EQM2080DetailTabType = "PLATINGLINE" | "PLATINGLINE2";

export const EQM2080_R01_DEFAULT_EQUIPMENT_CD = "PLATINGLINE";

export interface EQM2080_R01Filter {
	compCd: string;
	equipmentCd: string;
	equipmentNm: string;
	inspectDt: string;
	printDt: string;
}

export interface Store_EQM2080_R01 {
	EQM2080_R01Filter: EQM2080_R01Filter;
	EQM2080_R01PastFilter: EQM2080_R01Filter;
	setEQM2080_R01Filter: (filter: Partial<EQM2080_R01Filter>) => void;
	setEQM2080_R01PastFilter: (filter: EQM2080_R01Filter) => void;

	EQM2080_R01List: EQM2080_R01HeaderDto[];
	setEQM2080_R01List: (dataList: EQM2080_R01HeaderDto[]) => void;

	editEQM2080_R01: EQM2080_R01HeaderDto | null;
	setEditEQM2080_R01: (editData: EQM2080_R01HeaderDto | null) => void;

	selectedEQM2080_R01: EQM2080_R01HeaderDto | undefined;
	setSelectedEQM2080_R01: (data: EQM2080_R01HeaderDto | undefined) => void;
	initSelectedEQM2080_R01: () => void;

	activeDetailTab: EQM2080DetailTabType;
	setActiveDetailTab: (tab: EQM2080DetailTabType) => void;

	EQM2080_R01NIDetailList: EQM2080_R01DetailPivotDto[];
	setEQM2080_R01NIDetailList: (data: EQM2080_R01DetailPivotDto[]) => void;
	updateEQM2080_R01NIDetail: (valueIndex: number, data: EQM2080_R01DetailPivotDto) => void;

	originalEQM2080_R01NIDetailList: EQM2080_R01DetailPivotDto[];
	setOriginalEQM2080_R01NIDetailList: (data: EQM2080_R01DetailPivotDto[]) => void;

	deletedEQM2080_R01NIDetailList: string[];
	addDeletedEQM2080_R01NIDetailList: (idList: string[]) => void;
	removeDeletedEQM2080_R01NIDetailList: (idList?: string[]) => void;

	selectedEQM2080_R01NIDetailList: string[];
	setSelectedEQM2080_R01NIDetailList: (idList: string[]) => void;
	addSelectedEQM2080_R01NIDetailList: (idList: string[]) => void;
	removeSelectedEQM2080_R01NIDetailList: (idList?: string[]) => void;
	initSelectedEQM2080_R01NIDetailList: () => void;
	allSelectedEQM2080_R01NIDetailList: () => void;

	niDetailColumnState: ColumnState[] | null;
	setNiDetailColumnState: (state: ColumnState[] | null) => void;

	niDetailScrollTop: number | null;
	setNiDetailScrollTop: (top: number | null) => void;

	EQM2080_R01CNDetailList: EQM2080_R01DetailPivotDto[];
	setEQM2080_R01CNDetailList: (data: EQM2080_R01DetailPivotDto[]) => void;
	updateEQM2080_R01CNDetail: (valueIndex: number, data: EQM2080_R01DetailPivotDto) => void;

	originalEQM2080_R01CNDetailList: EQM2080_R01DetailPivotDto[];
	setOriginalEQM2080_R01CNDetailList: (data: EQM2080_R01DetailPivotDto[]) => void;

	deletedEQM2080_R01CNDetailList: string[];
	addDeletedEQM2080_R01CNDetailList: (idList: string[]) => void;
	removeDeletedEQM2080_R01CNDetailList: (idList?: string[]) => void;

	selectedEQM2080_R01CNDetailList: string[];
	setSelectedEQM2080_R01CNDetailList: (idList: string[]) => void;
	addSelectedEQM2080_R01CNDetailList: (idList: string[]) => void;
	removeSelectedEQM2080_R01CNDetailList: (idList?: string[]) => void;
	initSelectedEQM2080_R01CNDetailList: () => void;
	allSelectedEQM2080_R01CNDetailList: () => void;

	cnDetailColumnState: ColumnState[] | null;
	setCnDetailColumnState: (state: ColumnState[] | null) => void;

	cnDetailScrollTop: number | null;
	setCnDetailScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

export const defaultEQM2080_R01Filter: EQM2080_R01Filter = {
	compCd: "",
	equipmentCd: EQM2080_R01_DEFAULT_EQUIPMENT_CD,
	equipmentNm: "",
	inspectDt: formatDateYear4(new Date()) + "-" + formatDateMonth(new Date()),
	printDt: formatDate(new Date()),
};

const clonePivotList = (list: EQM2080_R01DetailPivotDto[]) =>
	list.map((row) => ({
		...row,
		dayValues: { ...row.dayValues },
		dayValues2: { ...row.dayValues2 },
		dayMeta: { ...row.dayMeta },
	}));

const createEmptyDetailTabState = () => ({
	detailList: [] as EQM2080_R01DetailPivotDto[],
	originalDetailList: [] as EQM2080_R01DetailPivotDto[],
	deletedDetailList: [] as string[],
	selectedDetailList: [] as string[],
	detailColumnState: null as ColumnState[] | null,
	detailScrollTop: null as number | null,
});

const initialDetailTabState = createEmptyDetailTabState();

export const useEQM2080_R01Store = create<Store_EQM2080_R01>((set) => ({
	EQM2080_R01Filter: defaultEQM2080_R01Filter,
	EQM2080_R01PastFilter: defaultEQM2080_R01Filter,
	setEQM2080_R01Filter: (update) =>
		set((state) => ({
			EQM2080_R01Filter: {
				...state.EQM2080_R01Filter,
				...update,
			},
		})),
	setEQM2080_R01PastFilter: (filter) =>
		set((state) => ({
			...state,
			EQM2080_R01PastFilter: filter,
		})),

	EQM2080_R01List: [],
	setEQM2080_R01List: (dataList) =>
		set((state) => ({
			...state,
			EQM2080_R01List: dataList,
		})),

	editEQM2080_R01: defaultEQM2080_R01HeaderDto,
	setEditEQM2080_R01: (editData) =>
		set((state) => ({
			...state,
			editEQM2080_R01: editData,
		})),

	selectedEQM2080_R01: undefined,
	setSelectedEQM2080_R01: (data) =>
		set((state) => ({
			...state,
			selectedEQM2080_R01: data,
			EQM2080_R01NIDetailList: [],
			originalEQM2080_R01NIDetailList: [],
			deletedEQM2080_R01NIDetailList: [],
			selectedEQM2080_R01NIDetailList: [],
			EQM2080_R01CNDetailList: [],
			originalEQM2080_R01CNDetailList: [],
			deletedEQM2080_R01CNDetailList: [],
			selectedEQM2080_R01CNDetailList: [],
		})),
	initSelectedEQM2080_R01: () =>
		set({
			selectedEQM2080_R01: undefined,
			editEQM2080_R01: defaultEQM2080_R01HeaderDto,
			EQM2080_R01NIDetailList: [],
			originalEQM2080_R01NIDetailList: [],
			deletedEQM2080_R01NIDetailList: [],
			selectedEQM2080_R01NIDetailList: [],
			EQM2080_R01CNDetailList: [],
			originalEQM2080_R01CNDetailList: [],
			deletedEQM2080_R01CNDetailList: [],
			selectedEQM2080_R01CNDetailList: [],
		}),

	activeDetailTab: EQM2080_R01_DEFAULT_EQUIPMENT_CD,
	setActiveDetailTab: (tab) => set({ activeDetailTab: tab }),

	EQM2080_R01NIDetailList: initialDetailTabState.detailList,
	setEQM2080_R01NIDetailList: (data) =>
		set((state) => ({
			...state,
			EQM2080_R01NIDetailList: data,
		})),
	updateEQM2080_R01NIDetail: (valueIndex, data) =>
		set((state) => ({
			...state,
			EQM2080_R01NIDetailList: state.EQM2080_R01NIDetailList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),

	originalEQM2080_R01NIDetailList: initialDetailTabState.originalDetailList,
	setOriginalEQM2080_R01NIDetailList: (list) =>
		set((state) => ({
			...state,
			originalEQM2080_R01NIDetailList: clonePivotList(list),
		})),

	deletedEQM2080_R01NIDetailList: initialDetailTabState.deletedDetailList,
	addDeletedEQM2080_R01NIDetailList: (idList) =>
		set((state) => {
			const deletedList = [...state.deletedEQM2080_R01NIDetailList];
			for (const valueIndex of idList) {
				const isInserted = state.EQM2080_R01NIDetailList.find(
					(item) => item.valueIndex === Number(valueIndex) && item.inserted,
				);
				const isModified = state.EQM2080_R01NIDetailList.find(
					(item) => item.valueIndex === Number(valueIndex) && item.isModified,
				);
				if (!isInserted && !isModified) {
					deletedList.push(valueIndex);
				}
			}
			return { ...state, deletedEQM2080_R01NIDetailList: deletedList };
		}),
	removeDeletedEQM2080_R01NIDetailList: (idList) =>
		set((state) => ({
			...state,
			deletedEQM2080_R01NIDetailList: state.deletedEQM2080_R01NIDetailList.filter(
				(value) => (idList ? !idList.includes(value) : false),
			),
		})),

	selectedEQM2080_R01NIDetailList: initialDetailTabState.selectedDetailList,
	setSelectedEQM2080_R01NIDetailList: (idList) =>
		set({ selectedEQM2080_R01NIDetailList: idList }),
	addSelectedEQM2080_R01NIDetailList: (idList) =>
		set((state) => ({
			...state,
			selectedEQM2080_R01NIDetailList: [
				...state.selectedEQM2080_R01NIDetailList,
				...idList,
			],
		})),
	removeSelectedEQM2080_R01NIDetailList: (idList) =>
		set((state) => ({
			...state,
			selectedEQM2080_R01NIDetailList: state.selectedEQM2080_R01NIDetailList.filter(
				(value) => (idList ? !idList.includes(value) : false),
			),
		})),
	initSelectedEQM2080_R01NIDetailList: () =>
		set({ selectedEQM2080_R01NIDetailList: [] }),
	allSelectedEQM2080_R01NIDetailList: () =>
		set((state) => ({
			...state,
			selectedEQM2080_R01NIDetailList: state.EQM2080_R01NIDetailList.map((item) =>
				item.valueIndex.toString(),
			),
		})),

	niDetailColumnState: initialDetailTabState.detailColumnState,
	setNiDetailColumnState: (niDetailColumnState) => set({ niDetailColumnState }),

	niDetailScrollTop: initialDetailTabState.detailScrollTop,
	setNiDetailScrollTop: (niDetailScrollTop) => set({ niDetailScrollTop }),

	EQM2080_R01CNDetailList: initialDetailTabState.detailList,
	setEQM2080_R01CNDetailList: (data) =>
		set((state) => ({
			...state,
			EQM2080_R01CNDetailList: data,
		})),
	updateEQM2080_R01CNDetail: (valueIndex, data) =>
		set((state) => ({
			...state,
			EQM2080_R01CNDetailList: state.EQM2080_R01CNDetailList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),

	originalEQM2080_R01CNDetailList: initialDetailTabState.originalDetailList,
	setOriginalEQM2080_R01CNDetailList: (list) =>
		set((state) => ({
			...state,
			originalEQM2080_R01CNDetailList: clonePivotList(list),
		})),

	deletedEQM2080_R01CNDetailList: initialDetailTabState.deletedDetailList,
	addDeletedEQM2080_R01CNDetailList: (idList) =>
		set((state) => {
			const deletedList = [...state.deletedEQM2080_R01CNDetailList];
			for (const valueIndex of idList) {
				const isInserted = state.EQM2080_R01CNDetailList.find(
					(item) => item.valueIndex === Number(valueIndex) && item.inserted,
				);
				const isModified = state.EQM2080_R01CNDetailList.find(
					(item) => item.valueIndex === Number(valueIndex) && item.isModified,
				);
				if (!isInserted && !isModified) {
					deletedList.push(valueIndex);
				}
			}
			return { ...state, deletedEQM2080_R01CNDetailList: deletedList };
		}),
	removeDeletedEQM2080_R01CNDetailList: (idList) =>
		set((state) => ({
			...state,
			deletedEQM2080_R01CNDetailList: state.deletedEQM2080_R01CNDetailList.filter(
				(value) => (idList ? !idList.includes(value) : false),
			),
		})),

	selectedEQM2080_R01CNDetailList: initialDetailTabState.selectedDetailList,
	setSelectedEQM2080_R01CNDetailList: (idList) =>
		set({ selectedEQM2080_R01CNDetailList: idList }),
	addSelectedEQM2080_R01CNDetailList: (idList) =>
		set((state) => ({
			...state,
			selectedEQM2080_R01CNDetailList: [
				...state.selectedEQM2080_R01CNDetailList,
				...idList,
			],
		})),
	removeSelectedEQM2080_R01CNDetailList: (idList) =>
		set((state) => ({
			...state,
			selectedEQM2080_R01CNDetailList: state.selectedEQM2080_R01CNDetailList.filter(
				(value) => (idList ? !idList.includes(value) : false),
			),
		})),
	initSelectedEQM2080_R01CNDetailList: () =>
		set({ selectedEQM2080_R01CNDetailList: [] }),
	allSelectedEQM2080_R01CNDetailList: () =>
		set((state) => ({
			...state,
			selectedEQM2080_R01CNDetailList: state.EQM2080_R01CNDetailList.map((item) =>
				item.valueIndex.toString(),
			),
		})),

	cnDetailColumnState: initialDetailTabState.detailColumnState,
	setCnDetailColumnState: (cnDetailColumnState) => set({ cnDetailColumnState }),

	cnDetailScrollTop: initialDetailTabState.detailScrollTop,
	setCnDetailScrollTop: (cnDetailScrollTop) => set({ cnDetailScrollTop }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () =>
		set((state) => ({
			...state,
			EQM2080_R01Filter: defaultEQM2080_R01Filter,
			EQM2080_R01PastFilter: defaultEQM2080_R01Filter,
			EQM2080_R01List: [],
			selectedEQM2080_R01: undefined,
			activeDetailTab: EQM2080_R01_DEFAULT_EQUIPMENT_CD,
			EQM2080_R01NIDetailList: [],
			originalEQM2080_R01NIDetailList: [],
			deletedEQM2080_R01NIDetailList: [],
			selectedEQM2080_R01NIDetailList: [],
			EQM2080_R01CNDetailList: [],
			originalEQM2080_R01CNDetailList: [],
			deletedEQM2080_R01CNDetailList: [],
			selectedEQM2080_R01CNDetailList: [],
			editEQM2080_R01: defaultEQM2080_R01HeaderDto,
			allCount: 0,
			niDetailScrollTop: null,
			cnDetailScrollTop: null,
		})),
}));
